// History slice — global undo/redo for keypoint edits.
//
// One stack across both sides and all frames. Each user-action pushes a single
// `HistoryEntry` (an array of `EditChange`s) so bulk operations like
// `setAllLocked` and `resetFrame` collapse to one undo step. The edits slice
// records entries by calling `pushHistory`; undo/redo here directly mutate the
// edits maps without re-recording.

import type { Draft } from 'immer';
import type { StateCreator } from 'zustand';
import type { EditChange, EditMap, HistoryEntry } from '../types';
import { unauditClearedFrames } from './edits';
import type { AppState } from './index';

const MAX_HISTORY = 200;

export interface HistorySlice {
  past: HistoryEntry[];
  future: HistoryEntry[];
  pushHistory: (entry: HistoryEntry) => void;
  undo: () => void;
  redo: () => void;
  clearHistory: () => void;
}

function pickMap(
  state: Draft<AppState>,
  side: 'front_left' | 'front_right' | 'left' | 'right'
): Draft<EditMap> {
  if (side === 'front_left') return state.editsFrontLeft;
  if (side === 'front_right') return state.editsFrontRight;
  if (side === 'left') return state.editsLeft;
  return state.editsRight;
}

function writeChange(
  state: Draft<AppState>,
  change: EditChange,
  direction: 'forward' | 'backward'
): void {
  const target = direction === 'forward' ? change.next : change.prev;
  const map = pickMap(state, change.side);
  if (target === null) {
    const frameMap = map[change.frame];
    if (!frameMap) return;
    delete frameMap[change.name];
    if (Object.keys(frameMap).length === 0) {
      delete map[change.frame];
    }
    return;
  }
  const frameMap = map[change.frame] ?? {};
  frameMap[change.name] = target;
  map[change.frame] = frameMap;
}

export const createHistorySlice: StateCreator<
  AppState,
  [['zustand/immer', never]],
  [],
  HistorySlice
> = (set) => ({
  past: [],
  future: [],
  pushHistory: (entry) =>
    set((s) => {
      if (entry.length === 0) return;
      // Coalesce drag-style continuations into the previous entry so a single
      // drag (which fires setKeypoint per pointermove) is one undo step.
      // Rule: same keypoint, both deltas are xy-only (visibility & locked
      // unchanged), and no redo discontinuity in between.
      const last = s.past[s.past.length - 1];
      const a = last && last.length === 1 ? last[0] : undefined;
      const b = entry.length === 1 ? entry[0] : undefined;
      if (
        s.future.length === 0 &&
        a !== undefined &&
        b !== undefined &&
        a.side === b.side &&
        a.frame === b.frame &&
        a.name === b.name &&
        a.next !== null &&
        b.next !== null &&
        b.prev !== null &&
        a.next.visibility === b.next.visibility &&
        a.next.locked === b.next.locked &&
        b.prev.visibility === b.next.visibility &&
        b.prev.locked === b.next.locked
      ) {
        a.next = b.next;
        return;
      }
      s.past.push(entry);
      if (s.past.length > MAX_HISTORY) {
        s.past.splice(0, s.past.length - MAX_HISTORY);
      }
      s.future.length = 0;
    }),
  undo: () =>
    set((s) => {
      const entry = s.past.pop();
      if (!entry) return;
      // Apply each change's `prev` in reverse order so internally-dependent
      // batches reverse cleanly.
      for (let i = entry.length - 1; i >= 0; i--) {
        const change = entry[i];
        if (change) writeChange(s, change, 'backward');
      }
      unauditClearedFrames(
        s,
        entry.map((c) => c.frame)
      );
      s.future.push(entry);
    }),
  redo: () =>
    set((s) => {
      const entry = s.future.pop();
      if (!entry) return;
      for (const change of entry) {
        writeChange(s, change, 'forward');
      }
      unauditClearedFrames(
        s,
        entry.map((c) => c.frame)
      );
      s.past.push(entry);
    }),
  clearHistory: () =>
    set((s) => {
      s.past.length = 0;
      s.future.length = 0;
    }),
});
